import React, { useState, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  createTheme,
  createFilterOptions,
  Autocomplete,
  Grid,
  TextField,
  CircularProgress,
  Box,
  Alert,
  Avatar,
  Chip,
} from "@mui/material";
import axios from "axios";
import { checkYearWithGpt, Legend } from "../utils/GlobalFunctions";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { getIconBySkillName } from "../utils/GlobalFunctions";

// Utility function moved outside component to prevent recreation on every render
const compareArrays = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

function TechRelevanceForm({
  setSkillLoading,
  setRelevanceResult,
  methods,
  DataDecryption,
  DataEncryption,
  setTopSkillValidity,
  setSynonyms,
  setAutoValue,
  shouldAutoFocus,
  setShouldAutoFocus,
  getEmail,
  skillLength,
  autoValue,
  topSkillValidity,
  deviceInfo,
  addSkill,
  onSubmit,
  setErrorWithStatus,
  setErrorAPI,
  location,
  getSkillOptions,
  skillLoading,
  shouldAutoClick,
  autoClickExecutedRef,
  onSkillValidationComplete,
}) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [open, setOpen] = useState(false); // if dropdown open?
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noOptions, setNoOptions] = useState(false);
  const shouldRunEffect = useRef(false);
  const cancelSkillToken = useRef(null);
  const textFieldRef = useRef(null);
  const [duplicateError, setDuplicateError] = useState(false);
  const isApiCallInProgress = useRef(false); // Prevent multiple simultaneous API calls
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const filter = createFilterOptions({
    stringify: (option) => option.name,
  });

  const [duplicateMessage, setDuplicateMessage] = useState(
    "Duplicate skill found and removed"
  );

  useEffect(() => {
    if (methods.getValues("primarySkill")?.length !== skillLength) {
      setRelevanceResult("");
    }

    // Skip if effect shouldn't run yet
    if (!shouldRunEffect.current) {
      shouldRunEffect.current = true;
      return;
    }

    // Skip if no skills
    if (autoValue.length === 0) {
      return;
    }

    // Skip if skills are the same as already validated ones
    if (compareArrays(autoValue, topSkillValidity)) {
      return;
    }

    // Only prevent multiple API calls for CVScore redirection (when auto-click is enabled)
    // Allow normal user interactions to work without restrictions
    const isCVScoreRedirection =
      shouldAutoClick || autoClickExecutedRef?.current;

    if (isCVScoreRedirection && isApiCallInProgress.current) {
      return;
    }

    // Set API call flag only for CVScore redirection
    if (isCVScoreRedirection) {
      isApiCallInProgress.current = true;
    }

    // Cancel previous request if exists
    if (cancelSkillToken.current) {
      cancelSkillToken.current.cancel("New request started");
    }

    const makeApiCall = async () => {
      try {
        cancelSkillToken.current = axios.CancelToken.source();
        setSkillLoading(true);
        setLoading(true);
        setValue("primarySkill", autoValue);

        const response = await axios.post(
          process.env.REACT_APP_API_BASE_URL + "/get-topSkillValidity",
          DataEncryption({
            primarySkill: autoValue,
            type: "skill-relevance",
          }),
          {
            cancelToken: cancelSkillToken.current.token,
            options: {
              type: "json",
            },
          }
        );

        const data = DataDecryption(response.data);
        let lastDuplicateIndex = -1;
        data.topPrimarySkill.forEach((skill, index) => {
          if (skill.isDuplicate) {
            lastDuplicateIndex = index;
          }
        });

        if (lastDuplicateIndex !== -1) {
          setDuplicateError(true);
          const filterSkills = data.topPrimarySkill.filter(
            (item) => item.isDuplicate === false
          );
          shouldRunEffect.current = false;
          setAutoValue(filterSkills);
          setTopSkillValidity(filterSkills);
          setValue("primarySkill", filterSkills);
          setSkillLoading(false);
          setSynonyms(filterSkills);
          textFieldRef.current.focus();
          setShouldAutoFocus(true);
        } else {
          shouldRunEffect.current = false;
          setSkillLoading(false);
          setAutoValue(data.topPrimarySkill);
          setTopSkillValidity(data.topPrimarySkill);
          setValue("primarySkill", data.topPrimarySkill);
          textFieldRef.current.focus();
          setShouldAutoFocus(true);
          setSynonyms(data.topPrimarySkill);
        }
        setLoading(false);

        // Reset API call flag only for CVScore redirection
        if (isCVScoreRedirection) {
          isApiCallInProgress.current = false;
        }

        // Notify parent component that skill validation is complete
        if (onSkillValidationComplete && isCVScoreRedirection) {
          onSkillValidationComplete();
        }

        const AlertDuplicate = setTimeout(() => {
          setDuplicateError(false);
        }, 3000);
      } catch (error) {
        // Reset API call flag on error only for CVScore redirection
        if (isCVScoreRedirection) {
          isApiCallInProgress.current = false;
        }

        // Ignore cancel errors — these happen during normal cleanup
        // when autoValue changes and the previous request is aborted
        if (axios.isCancel(error)) {
          setLoading(false);
          return;
        }

        setLoading(false);
        setSkillLoading(false);
        let errorMessage = "An unexpected error occurred";
        let errorStatus = null;

        // Handle API-specific errors
        if (error?.response) {
          errorStatus = error.response.status;

          if (
            errorStatus === 500 &&
            error?.response?.data?.message === "Email verification failed"
          ) {
            errorMessage = "Email not valid !!";
            methods.setError("email", {
              type: "validation",
              message: errorMessage,
            });
            setRelevanceResult("");
          } else if (errorStatus === 404) {
            errorMessage = "API endpoint not found";
          } else if (errorStatus >= 400) {
            errorMessage = "Client-side error occurred";
          } else if (errorStatus >= 500) {
            errorMessage = "Internal server error occurred";
          }
        } else {
          // Handle network errors
          errorMessage = "Network error, please check your connection";
        }
        setErrorWithStatus({ message: errorMessage, status: errorStatus });
        setErrorAPI(true);
      }
    };

    makeApiCall();

    // Cleanup function to cancel ongoing requests
    return () => {
      if (cancelSkillToken.current) {
        cancelSkillToken.current.cancel("Component unmounting");
      }
      // Only reset API call flag for CVScore redirection
      if (isCVScoreRedirection) {
        isApiCallInProgress.current = false;
      }
    };
  }, [
    autoValue,
    topSkillValidity,
    skillLength,
    // Removed methods, setValue, and other function dependencies that cause unnecessary re-runs
    // These functions are stable and don't need to be in dependencies
  ]);

  // useEffect(() => {
  //   if (
  //     relevanceResult === "" &&
  //     textFieldRef.current &&
  //     document.activeElement !== textFieldRef.current
  //   ) {
  //     textFieldRef.current.focus();
  //   }
  // }, []);
  return (
    <React.Fragment>
      <Grid
        container
        justifyContent="center"
        alignItems={"flex-end"}
        spacing={{ xs: 2, md: 3 }}
      >
        <Grid item xs={12} md={12} lg={6} sx={{ mb: 1.6 }}>
          {autoValue?.length > 0 && <Legend />}

          <Controller
            name={"primarySkill"}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                noOptionsText={
                  noOptions ? "No options found" : "Start typing to get options"
                }
                {...field}
                open={open}
                onOpen={() => {
                  setNoOptions(false);
                  setOpen(true);
                  setData([]);
                }}
                onClose={() => {
                  setOpen(false);
                }}
                loading={loading}
                multiple
                autoComplete={true}
                isOptionEqualToValue={(option, value) =>
                  option.name === value.name
                }
                value={autoValue}
                onChange={(event, newValue) => {
                  // Update the value of primarySkill
                  if (
                    newValue.slice(-1)[0] &&
                    newValue.slice(-1)[0].inputValue != undefined
                  ) {
                    setAutoValue([
                      ...autoValue,
                      {
                        name: newValue.slice(-1)[0].inputValue,
                        other: true,
                      },
                    ]);
                  } else {
                    setAutoValue(newValue);
                  }

                  // Clear synonyms if primarySkill is empty
                  if (newValue.length === 0) {
                    setSynonyms([]);
                  }

                  setData([]);
                }}
                filterSelectedOptions={true}
                onInputChange={(event, value, reason) => {
                  if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                  }

                  if (reason !== "input") {
                    setLoading(false);
                    return;
                  }

                  setNoOptions(true);
                  const cleanedValue = value?.replace(/\\/g, "").trim();

                  if (cleanedValue) {
                    setLoading(true);
                    debounceTimerRef.current = setTimeout(() => {
                      const encodedSkillName = Array.from(cleanedValue)
                        .map((char) => {
                          const isSpecialSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(
                            char
                          );
                          return isSpecialSymbol
                            ? encodeURIComponent(char)
                            : char;
                        })
                        .join("");

                      const endpoint = `/get-relevanceSkill?skills=${encodedSkillName}`;

                      getSkillOptions(
                        setData,
                        endpoint,
                        setLoading,
                        setErrorWithStatus,
                        setErrorAPI
                      );
                    }, 800);
                  } else {
                    setLoading(false);
                  }
                }}
                id="skills-autocomplete"
                options={data}
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.name;
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                filterOptions={(options, params) => {
                  const isExisting = options.some(
                    (option) =>
                      params.inputValue.toLowerCase() ===
                      option.name.toLowerCase()
                  );
                  const paramsValue = params.inputValue.trim();
                  params.inputValue = params.inputValue.trim();
                  const filtered = filter(options, params);

                  const selectedValue = getValues("primarySkill");
                  const filteredSelectedValue = selectedValue?.find(
                    (item) =>
                      item.name.toLowerCase() ===
                      params.inputValue.toLowerCase()
                  );

                  let filterData = [];
                  const secondSelectedSkills = getValues("primarySkill");

                  filterData = new Set(
                    secondSelectedSkills?.map((skill) =>
                      skill.name.toLowerCase()
                    )
                  );

                  if (
                    params.inputValue !== "" &&
                    !isExisting &&
                    !filteredSelectedValue &&
                    loading === false
                  ) {
                    if (!filterData.has(params.inputValue.toLowerCase())) {
                      filtered.push({
                        inputValue: paramsValue,
                        name: `Add "${paramsValue}" Skill`,
                      });
                    }
                  }
                  return filtered;
                }}
                renderOption={(props, option, { inputValue }) => {
                  const matches = match(option.name, inputValue.trim());
                  const parts = parse(option.name, matches);

                  return (
                    <li {...props} key={option.name}>
                      <div>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              color: part.highlight ? "red" : "inherit",
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: { xs: "22px", lg: "22px !important" },
                      },
                      "& .MuiFormHelperText-root": {
                        color:
                          errors?.primarySkill || autoValue?.length > 5
                            ? "#d32f2f !important"
                            : "#666",
                        fontSize: "13px",
                        fontWeight: autoValue?.length > 5 ? 600 : 500,
                        ml: "14px",
                        mt: "4px",
                        transition: "color 0.2s ease, font-weight 0.2s ease",
                      },
                    }}
                    inputRef={textFieldRef}
                    autoFocus={shouldAutoFocus}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    error={errors?.primarySkill ? true : false}
                    helperText={
                      errors?.primarySkill
                        ? "Skills cannot be empty"
                        : "Add 3 to 5 skills for better results"
                    }
                    label={"Select multiple options"}
                    id="skills-autocomplete"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const yearInfo = checkYearWithGpt(option);
                    const iconUrl = getIconBySkillName(option.imgPath);
                    const isFixedSize = value.length <= 10;

                    // Compute abbreviation upfront so it can be used as
                    // fallback for both image-Avatar and text-Avatar
                    const getFirstValidChar = (word) => {
                      for (let i = 0; i < word.length; i++) {
                        const char = word.charAt(i).toUpperCase();
                        if (/^[a-zA-Z0-9]$/.test(char)) {
                          return char;
                        }
                      }
                      return "";
                    };

                    let abbreviation = "";
                    if (option.name) {
                      const words = option.name
                        .split(" ")
                        .filter((word) => word.length > 0);

                      if (words.length === 1) {
                        abbreviation = getFirstValidChar(words[0]);
                      } else if (words.length === 2) {
                        abbreviation = words
                          .map((word) => getFirstValidChar(word))
                          .join("");
                      } else if (words.length === 3) {
                        abbreviation = words
                          .map((word) => getFirstValidChar(word))
                          .join("");
                      } else if (words.length > 3) {
                        abbreviation = getFirstValidChar(words[0]);
                      }

                      if (!abbreviation) {
                        abbreviation = words
                          .map((word) => getFirstValidChar(word))
                          .join("");
                      }
                    }

                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?._id}
                        label={option?.name}
                        sx={{
                          backgroundColor:
                            yearInfo?.barSecondaryColor ||
                            "rgba(0, 0, 0, 0.08)",
                          color:
                            yearInfo?.label === "Default"
                              ? "#000000"
                              : "#ffffff",
                          border:
                            `1px solid ${yearInfo?.color}` ||
                            "rgba(0, 0, 0, 0.87)",
                          width: "auto",
                          height: isFixedSize ? "45px !important" : "auto",
                          fontSize: isFixedSize ? "15px !important" : "13px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        avatar={
                            <Avatar
                              src={iconUrl}
                              alt={option?.name}
                              slotProps={{
                                img: {
                                  loading: "lazy",
                                },
                              }}
                              sx={{
                                fontSize: isFixedSize
                                  ? "13px !important"
                                  : "10px !important",
                                width: isFixedSize
                                  ? "25px !important"
                                  : "15px !important",
                                height: isFixedSize
                                  ? "25px !important"
                                  : "15px !important",
                                my: isFixedSize ? "0" : "3px",
                                backgroundColor: "#ffffff",
                                p: "5px",
                              }}
                            >
                              {abbreviation}
                            </Avatar>
                        }
                      />
                    );
                  })
                }
                disabled={skillLoading}
              />
            )}
          />
          {duplicateError && (
            <Box sx={{ py: 0.5, mt: 1 }}>
              <Alert severity="warning">{duplicateMessage}</Alert>
            </Box>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default TechRelevanceForm;
